
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/auth';
import { healthStore } from './healthStore';
import { performHealthCheck } from './checkHealth';
import { UseApiHealthOptions, UseApiHealthResult, HealthData } from './types';

export const useApiHealth = (options: UseApiHealthOptions = {}): UseApiHealthResult => {
  const { 
    // Common options
    periodicCheck = false, // Changed default to false
    initialDelayMs = 0,
    timeoutMs = 10000, // Default timeout of 10 seconds
    
    // Standard mode options
    checkInterval = 30000,  // Default: check every 30 seconds when authenticated
    retryInterval = 5000,   // Default: retry every 5 seconds when down
    maxRetries = 0,         // Default: unlimited retries
    
    // Progressive mode options
    progressive = false,
    initialInterval = 5000,
    maxInterval = 60000,
    intervalStep = 5000,
    checksPerPhase = 3,
    
    // API mode option (use REST API instead of WebSocket)
    useApi = false
  } = options;
  
  const { isAuthenticated } = useAuth();
  
  // Use internal state for component updates, synced with the global store
  const [isHealthy, setIsHealthy] = useState<boolean | null>(healthStore.getHealthStatus());
  const [isChecking, setIsChecking] = useState<boolean>(healthStore.isChecking());
  const [healthData, setHealthData] = useState(healthStore.getHealthData());
  
  // Progressive check state
  const [retryCount, setRetryCount] = useState<number>(0);
  const [currentInterval, setCurrentInterval] = useState<number>(initialInterval);
  const [showCheckNowButton, setShowCheckNowButton] = useState<boolean>(false);
  
  // Set the correct mode based on props
  const mode = useMemo(() => {
    const newMode = progressive ? 'progressive' : 'standard';
    healthStore.setMode(newMode);
    return newMode;
  }, [progressive]);
  
  // Subscribe to global store changes
  useEffect(() => {
    const unsubscribe = healthStore.subscribe(() => {
      setIsHealthy(healthStore.getHealthStatus());
      setIsChecking(healthStore.isChecking());
      setHealthData(healthStore.getHealthData());
    });
    
    return unsubscribe;
  }, []);

  // Function to update health data
  const updateHealthData = useCallback((data: HealthData | null) => {
    healthStore.setHealthData(data);
  }, []);
  
  // Calculate the dynamic interval for progressive mode
  const calculateProgressiveInterval = useCallback((count: number) => {
    const phaseNumber = Math.floor(count / checksPerPhase);
    const newInterval = Math.min(initialInterval + (phaseNumber * intervalStep), maxInterval);
    return newInterval;
  }, [initialInterval, intervalStep, maxInterval, checksPerPhase]);
  
  // Main health check function
  const checkHealth = useCallback(async (force = false) => {
    return performHealthCheck({
      force,
      useApi,
      timeoutMs, // Pass the timeout option
      onSuccess: (healthy) => {
        // Reset retry count if healthy in progressive mode
        if (progressive && healthy) {
          setRetryCount(0);
        }
      }
    });
  }, [progressive, useApi, timeoutMs]);
  
  // Run check immediately with initial delay - ONLY if periodicCheck is true
  useEffect(() => {
    if (!periodicCheck) {
      return;
    }
    
    const initialCheck = setTimeout(() => {
      checkHealth();
    }, initialDelayMs);
    
    return () => {
      clearTimeout(initialCheck);
    };
  }, [checkHealth, initialDelayMs, periodicCheck]);
  
  // Setup periodic checks
  useEffect(() => {
    if (!periodicCheck) {
      return;
    }
    
    let intervalId: number | NodeJS.Timeout;
    
    if (mode === 'progressive' && isHealthy === false) {
      // Progressive mode (used on login page) when API is down
      
      // Show "check now" button after a few failed attempts
      if (retryCount > 2) {
        setShowCheckNowButton(true);
      }
      
      // Calculate dynamic interval based on retry count
      const dynamicInterval = calculateProgressiveInterval(retryCount);
      setCurrentInterval(dynamicInterval);
      
      // Set up the interval
      intervalId = setInterval(() => {
        checkHealth();
        setRetryCount(prev => prev + 1);
      }, dynamicInterval);
      
    } else if (mode === 'standard' && isHealthy === false) {
      // Standard mode (used when logged in) when API is down
      
      // Retry more frequently with 5-second interval
      intervalId = setInterval(() => {
        checkHealth();
        
        // Count retries if there's a max retry limit
        if (maxRetries > 0) {
          setRetryCount(prev => {
            const nextCount = prev + 1;
            if (nextCount >= maxRetries) {
              clearInterval(intervalId);
            }
            return nextCount;
          });
        }
      }, retryInterval);
      
    } else {
      // Normal interval when healthy (both modes)
      intervalId = setInterval(() => {
        checkHealth();
      }, isAuthenticated ? checkInterval : Math.min(checkInterval, 60000));
    }
    
    return () => {
      clearInterval(intervalId);
    };
  }, [
    periodicCheck,
    mode,
    isHealthy,
    retryCount,
    checkHealth,
    calculateProgressiveInterval,
    checkInterval,
    retryInterval,
    maxRetries,
    isAuthenticated
  ]);
  
  // Function to manually check status now (for the "Check status now" button)
  const checkStatusNow = useCallback(() => {
    return checkHealth(true);
  }, [checkHealth]);
  
  // Return different results based on mode
  if (progressive) {
    return {
      isHealthy,
      isChecking,
      healthData,
      checkHealth: checkStatusNow,
      retryCount,
      currentInterval,
      showCheckNowButton,
      checkStatusNow,
      setHealthData: updateHealthData, // Expose setHealthData function
    };
  }
  
  return {
    isHealthy,
    isChecking,
    healthData,
    checkHealth: checkStatusNow,
    setHealthData: updateHealthData, // Expose setHealthData function
  };
};
